import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const CodeSnippetHelper = () => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCopy = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const codeSnippets = {
    setup: {
      title: '1. Setup & Installation',
      description: 'Install and configure the library in your project (Private GitHub Package)',
      snippets: [
        {
          label: 'Step 1: Create .npmrc file',
          language: 'bash',
          code: `# Create .npmrc in your project root
@datum-ai-labs:registry=https://npm.pkg.github.com
//npm.pkg.github.com/:_authToken=YOUR_GITHUB_TOKEN`
        },
        {
          label: 'Step 2: Install Package (Production)',
          language: 'bash',
          code: `npm install @datum-ai-labs/react-widgets`
        },
        {
          label: 'Step 2: Install Package (Test/Beta)',
          language: 'bash',
          code: `npm install @datum-ai-labs/react-widgets@beta`
        },
        {
          label: 'Step 3: Setup Configuration',
          language: 'tsx',
          code: `import { setupDatumart } from '@datum-ai-labs/react-widgets';

// Add this in your root layout or _app.tsx
setupDatumart({
  apiUrl: process.env.NEXT_PUBLIC_API_URL,
  imageSourcePath: process.env.NEXT_PUBLIC_IMAGE_SOURCE_PATH,
  onAnalyticsEvent: (event) => {
    console.log('Datum Event:', event);
    // Push to GA4, GTM, or your analytics
  }
});`
        }
      ]
    },
    blogWidget: {
      title: '2. Blog Widget (Grid View)',
      description: 'Display a grid of blog posts with category filtering',
      snippets: [
        {
          label: 'Basic Usage',
          language: 'tsx',
          code: `import { BlogWidget } from '@datum-ai-labs/react-widgets';

// Show all posts
<BlogWidget 
  category="all" 
  limit={10} 
  theme="light" 
/>`
        },
        {
          label: 'Filter by Category',
          language: 'tsx',
          code: `import { BlogWidget } from '@datum-ai-labs/react-widgets';

// Show only technology posts
<BlogWidget 
  category="technology" 
  limit={6} 
  theme="dark" 
/>`
        },
        {
          label: 'With Custom Styling (All Properties)',
          language: 'tsx',
          code: `import { BlogWidget } from '@datum-ai-labs/react-widgets';

<BlogWidget 
  category="all"
  limit={10}
  theme="dark"
  styles={{
    backgroundColor: "#050B14",  // Main container background
    cardBg: "#1a1a2e",           // Individual card background
    headingColor: "#00AEEF",     // Card title color
    textColor: "#E2E8F0",        // Card excerpt text
    authorColor: "#FFFFFF",      // Author name color
    dateColor: "#9CA3AF",        // Date text color
    buttonColor: "#00AEEF",      // Read More button color
    excerptColor: "#CBD5E1"      // Excerpt text color
  }}
  onReadMore={(post) => router.push(\`/blog/\${post.slug}\`)}
/>`
        }
      ]
    },
    blogDetail: {
      title: '3. Blog Detail (Single Post)',
      description: 'Display a full blog post with optional features',
      snippets: [
        {
          label: 'Basic Usage',
          language: 'tsx',
          code: `import { BlogDetail } from '@datum-ai-labs/react-widgets';

// In your [slug]/page.tsx
<BlogDetail 
  slug={params.slug} 
  theme="light"
/>`
        },
        {
          label: 'With All Features',
          language: 'tsx',
          code: `import { BlogDetail } from '@datum-ai-labs/react-widgets';

<BlogDetail 
  slug={params.slug}
  theme="dark"
  showSocial={true}
  showRelated={true}
  showMustRead={true}
  onBack={() => router.back()}
  onReadMore={(post) => router.push(\`/blog/\${post.slug}\`)}
/>`
        },
        {
          label: 'With Custom Styling (All Properties)',
          language: 'tsx',
          code: `import { BlogDetail } from '@datum-ai-labs/react-widgets';

<BlogDetail 
  slug={params.slug}
  theme="dark"
  showSocial={true}
  showRelated={true}
  showMustRead={true}
  styles={{
    backgroundColor: "#050B14",  // Main page background
    headingColor: "#00AEEF",     // Title color
    textColor: "#E2E8F0",        // Paragraph text
    authorColor: "#FFFFFF",      // Author name
    dateColor: "#9CA3AF",        // Date text
    buttonColor: "#00AEEF",      // Read More arrow / Accents
    iconColor: "#FFFFFF"         // Social Share icons
  }}
  relatedCardStyles={{
    cardBg: "#1a1a2e",           // Related card background
    headingColor: "#F97316",     // Related card title
    textColor: "#E2E8F0",        // Related card text
    authorColor: "#FFFFFF",      // Related card author
    dateColor: "#9CA3AF",        // Related card date
    buttonColor: "#1E3A8A",      // Related card button
    excerptColor: "#CBD5E1"      // Related card excerpt
  }}
  onBack={() => router.back()}
  onReadMore={(post) => router.push(\`/blog/\${post.slug}\`)}
/>`
        }
      ]
    },
    events: {
      title: '4. Events Management',
      description: 'Display events with upcoming badges and date ranges',
      snippets: [
        {
          label: 'Show Upcoming Events Only',
          language: 'tsx',
          code: `import { BlogWidget } from '@datum-ai-labs/react-widgets';

<BlogWidget
  category="event"
  enableEventFeatures={true}
  showUpcomingOnly={true}
  theme="light"
  limit={10}
/>`
        },
        {
          label: 'Separate Upcoming & Past Events',
          language: 'tsx',
          code: `import { BlogWidget } from '@datum-ai-labs/react-widgets';

<BlogWidget
  category="event"
  enableEventFeatures={true}
  separateUpcoming={true}
  theme="light"
/>`
        },
        {
          label: 'Event Detail Page',
          language: 'tsx',
          code: `import { BlogDetail } from '@datum-ai-labs/react-widgets';

<BlogDetail
  slug={params.slug}
  theme="light"
  showSocial={true}
  showRelated={true}
  showMustRead={true}
/>`
        }
      ]
    },
    headless: {
      title: '5. Headless Hooks (Custom UI)',
      description: 'Use hooks to fetch data and build your own UI',
      snippets: [
        {
          label: 'useBlogs Hook',
          language: 'tsx',
          code: `import { useBlogs } from '@datum-ai-labs/react-widgets';

const CustomBlog = () => {
  const { blogs, loading, error } = useBlogs({ 
    category: 'technology',
    limit: 5 
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;
  
  return (
    <ul>
      {blogs.map(post => (
        <li key={post.id}>
          <h3>{post.title}</h3>
          <p>{post.excerpt}</p>
        </li>
      ))}
    </ul>
  );
};`
        },
        {
          label: 'useBlogDetail Hook',
          language: 'tsx',
          code: `import { useBlogDetail } from '@datum-ai-labs/react-widgets';

const CustomPostDetail = ({ slug }) => {
  const { post, loading, error } = useBlogDetail({ slug });

  if (loading) return <p>Loading...</p>;
  if (!post) return <p>Post not found</p>;

  return (
    <article>
      <h1>{post.title}</h1>
      <div dangerouslySetInnerHTML={{ __html: post.content }} />
    </article>
  );
};`
        },
        {
          label: 'BlogCard Component',
          language: 'tsx',
          code: `import { BlogCard, useBlogs } from '@datum-ai-labs/react-widgets';

const CustomGrid = () => {
  const { blogs } = useBlogs({ category: 'technology', limit: 6 });
  
  return (
    <div className="my-custom-grid">
      {blogs.map(post => (
        <BlogCard 
          key={post.id}
          post={post}
          theme="light"
          onReadMore={(post) => navigate(\`/blog/\${post.slug}\`)}
          enableEventFeatures={true}
        />
      ))}
    </div>
  );
};`
        },
        {
          label: '📊 Track Blog/Event View',
          language: 'tsx',
          code: `import { useBlogDetail, trackBlogView } from '@datum-ai-labs/react-widgets';
import { useEffect } from 'react';

const CustomBlogDetail = ({ slug }) => {
  const { post, loading, error } = useBlogDetail({ slug });

  // Track view when post loads
  useEffect(() => {
    if (post) {
      trackBlogView({
        blogId: post.id,
        blogTitle: post.title,
        blogSlug: post.slug || '',
        category: post.category,
        author: post.author,
        tags: post.tags?.join(', '),
        publishedDate: post.publishDate,
        isEvent: post.isEvent,
        eventStartDate: post.eventStartDate,
        eventEndDate: post.eventEndDate,
        eventLocation: post.eventLocation,
      });
    }
  }, [post]);

  if (loading) return <p>Loading...</p>;
  if (!post) return <p>Not found</p>;

  return (
    <article>
      <h1>{post.title}</h1>
      <div dangerouslySetInnerHTML={{ __html: post.content }} />
    </article>
  );
};`
        },
        {
          label: '📤 Track Social Share',
          language: 'tsx',
          code: `import { trackBlogShare } from '@datum-ai-labs/react-widgets';

const CustomShareButton = ({ post }) => {
  const handleShare = (platform) => {
    // Track the share event
    trackBlogShare({
      blogId: post.id,
      blogTitle: post.title,
      blogSlug: post.slug || '',
      platform: platform, // 'facebook' | 'twitter' | 'linkedin' | 'whatsapp'
    });

    // Open share dialog
    const shareUrls = {
      facebook: \`https://www.facebook.com/sharer/sharer.php?u=\${encodeURIComponent(window.location.href)}\`,
      twitter: \`https://twitter.com/intent/tweet?url=\${encodeURIComponent(window.location.href)}\`,
      linkedin: \`https://www.linkedin.com/shareArticle?mini=true&url=\${encodeURIComponent(window.location.href)}\`,
      whatsapp: \`https://api.whatsapp.com/send?text=\${encodeURIComponent(window.location.href)}\`,
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div>
      <button onClick={() => handleShare('facebook')}>Share on Facebook</button>
      <button onClick={() => handleShare('twitter')}>Share on Twitter</button>
      <button onClick={() => handleShare('linkedin')}>Share on LinkedIn</button>
      <button onClick={() => handleShare('whatsapp')}>Share on WhatsApp</button>
    </div>
  );
};`
        },
        {
          label: '🖱️ Track Card Click',
          language: 'tsx',
          code: `import { useBlogs, trackBlogClick } from '@datum-ai-labs/react-widgets';

const CustomBlogGrid = () => {
  const { blogs, loading, error } = useBlogs({ 
    category: 'technology', 
    limit: 6 
  });

  const handlePostClick = (post) => {
    // Track the click event
    trackBlogClick({
      blogId: post.id,
      blogTitle: post.title,
      blogSlug: post.slug || '',
      category: post.category,
      clickLocation: 'blog_grid', // Where the click happened
      sourceBlogId: undefined, // Optional: ID of source blog
      sourceBlogTitle: undefined, // Optional: Title of source blog
    });

    // Navigate to the post
    window.location.href = \`/blog/\${post.slug}\`;
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="grid">
      {blogs.map(post => (
        <div key={post.id} onClick={() => handlePostClick(post)}>
          <h3>{post.title}</h3>
          <p>{post.excerpt}</p>
        </div>
      ))}
    </div>
  );
};`
        }
      ]
    }
  };

  const tabData = Object.keys(codeSnippets);

  return (
    <>
      <Tooltip title="Code Helper" arrow>
        <Button
          onClick={handleOpen}
          sx={{
            borderRadius: '12px',
            color: '#0d3559',
            minWidth: '40px',
            width: '40px',
            height: '40px',
            p: 0,
            '&:hover': {
              bgcolor: 'rgba(13, 53, 89, 0.08)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CodeIcon sx={{ fontSize: 22 }} />
        </Button>
      </Tooltip>

      <Modal
        open={open}
        onClose={handleClose}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            width: '90%',
            maxWidth: 1100,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid #F1F3F9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: '#F8F9FE'
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1F2937', mb: 0.5 }}>
                📦 Widget Library Code Helper
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Copy-paste ready code snippets for @datum-ai-labs/react-widgets
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: '#525F7F' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: '48px'
                },
                '& .Mui-selected': {
                  color: '#0d3559 !important'
                }
              }}
            >
              {tabData.map((key) => (
                <Tab key={key} label={codeSnippets[key].title} />
              ))}
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 4 }}>
            {tabData.map((key, tabIndex) => (
              <Box key={key} hidden={activeTab !== tabIndex}>
                <Typography variant="body1" sx={{ color: '#6B7280', mb: 3 }}>
                  {codeSnippets[key].description}
                </Typography>

                {codeSnippets[key].snippets.map((snippet, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: '#1F2937' }}
                        >
                          {snippet.label}
                        </Typography>
                        <Chip
                          label={snippet.language}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(13, 53, 89, 0.1)',
                            color: '#0d3559'
                          }}
                        />
                      </Box>
                      <Button
                        size="small"
                        startIcon={
                          copiedIndex === `${tabIndex}-${index}` ? (
                            <CheckIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          )
                        }
                        onClick={() =>
                          handleCopy(snippet.code, `${tabIndex}-${index}`)
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          color:
                            copiedIndex === `${tabIndex}-${index}`
                              ? '#2e7d32'
                              : '#0d3559',
                          '&:hover': {
                            bgcolor: 'rgba(13, 53, 89, 0.08)'
                          }
                        }}
                      >
                        {copiedIndex === `${tabIndex}-${index}`
                          ? 'Copied!'
                          : 'Copy'}
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: '#F8F9FA',
                        borderRadius: '12px',
                        p: 2,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                        color: '#1F2937',
                        border: '2px solid #0d3559'
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {snippet.code}
                      </pre>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #F1F3F9',
              bgcolor: '#F8F9FE',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              💡 Tip: Click "Copy" to copy code to clipboard
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                bgcolor: '#0d3559',
                borderRadius: '10px',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                '&:hover': { bgcolor: '#0a2a47' }
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default CodeSnippetHelper;
